import React from 'react';

import * as Analytics from 'analytics/Analytics';
import {AnalyticsEvent} from 'analytics/Analytics';
import DataConnector from 'connect/DataConnector';
import {AnalyticsMetadataForContext, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {SchemalessBulkImportSupportedOligoAwareTypes} from 'constants/RegistryConstants';
import {Source} from 'constants/SourceConstants';
import * as Context from 'Context';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import SampleSchemaStore from 'samples/SampleSchemaStore';

import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  getAnalyticsModuleFromImportType,
  IMPORT_TYPE,
  Pane,
  PaneKey,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModal from './SpreadsheetBulkImportModal';
import SpreadsheetBulkImportStore, {SpreadsheetBulkImportState} from './SpreadsheetBulkImportStore';

class SpreadsheetBulkImportModalContainerConnector extends DataConnector<
  SpreadsheetBulkImportModalContainerProps,
  EnhancedProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [SpreadsheetBulkImportStore, SampleSchemaStore];

  enhanceProps(): EnhancedProps {
    const storeState = this.spreadsheetBulkImportStore().getState();
    return {storeState};
  }
}

interface EnhancedProps {
  storeState: SpreadsheetBulkImportState;
}

export interface SpreadsheetBulkImportModalContainerProps {
  footer?: React.ReactNode;
  importItemWord?: string;
  // Note: Use sparingly; strive to keep Spreadsheet components importType-agnostic.
  importType?: IMPORT_TYPE;
  // Settings to initalize in the store state.
  initialSettings?: {
    initialBatchSchemaId?: string | null;
    initialContainerSchemaId?: string | null;
    initialSchemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes;
    initialFolder?: {
      folderId: string | null;
      registryId?: string | null;
    } | null;
    initialRegistry?: Source | null;
    initialResultSchemaId?: string | null;
    initialTagSchemaId?: string | null;
    initialSchemaInterfaceId?: string | null;
    initialTableColumnNames?: Array<string>;
    initialSampleGroupSchemaId?: string | null;
    initialPlateSchemaId?: string | null;
    initialBoxSchemaId?: string | null;
    initialProcessTaskSchemaId?: string | null;
    initialProcessOutputSchemaId?: string | null;
    initialTrialSubjectSchemaId?: string | null;
    initialRawText?: string;
    initialPaneKey?: PaneKey;
    initialDataTableKeyFields?: Array<COLUMN_TYPE>;
  };
  onImportFromSpreadsheet: () => void;
  onRequestClose: () => void;
  // Panes should be an array specifying the order of panes in the flow. Each paneKey maps to a
  // pane component, and configuration should be an object configuring a given pane for that flow.
  // Configuration is usually set from *ModalPane.configure(...), which validates that the pane
  // was correctly configured and allows providing default values. The validation is now enforced
  // by Typescript as well, so a configure(...) method is only necessary for setting defaults.
  panes: Array<Pane>;
  title: string;
  isSchemaRequired?: boolean;
  analyticsMetadata?: AnalyticsMetadataForContext;
  complexPolymerSchemaConfigurations?: Array<ComplexPolymerSchemaConfigGraphQL>;
  maxAllowedRowsToImport?: number;
  inferDataTypes?: boolean;
}

export class SpreadsheetBulkImportModalContainer extends React.Component<
  SpreadsheetBulkImportModalContainerProps & EnhancedProps
> {
  static defaultProps = {
    importItemWord: 'Benchling file',
    isSchemaRequired: true,
  };

  override componentDidMount(): void {
    SpreadsheetBulkImportActions.beginImportFlow({
      importType: this.props.importType,
      panes: this.props.panes,
      ...this.props.initialSettings,
    });
    this.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL);
  }

  private trackEventWithModule = (event: AnalyticsEvent, includeSubmitMetadata: boolean = false): void => {
    const module = getAnalyticsModuleFromImportType(this.props.importType);
    const includedStorableNameColumn = this.props.storeState.fileData?.columns.some(
      (column) => column.type === COLUMN_TYPE.STORABLE_NAME
    );
    const entityNameColumns: Array<COLUMN_TYPE> = [COLUMN_TYPE.FILENAME, COLUMN_TYPE.NEW_BIOENTITY_NAME];
    const includedEntityNameColumn = this.props.storeState.fileData?.columns.some((column) =>
      entityNameColumns.includes(column.type)
    );
    const metadata = {
      ...this.props.analyticsMetadata,
      importType: this.props.importType,
      modalType: 'spreadsheet_import',
      ...(includeSubmitMetadata && {includedStorableNameColumn, includedEntityNameColumn}),
    };
    if (module != null) {
      Analytics.trackEventWithModule(event, module, metadata);
    }
  };

  // Used in tests
  handleImportFromSpreadsheet = (): void => {
    Context.usingAsync(
      {analyticsMetadata: this.props.analyticsMetadata ?? {}},
      null,
      this.props.onImportFromSpreadsheet
    );
    this.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, true);
    this.props.onRequestClose();
  };

  // Used in tests
  handleOnRequestClose = (): void => {
    this.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL);
    this.props.onRequestClose();
  };

  override render(): JSX.Element | null {
    // Don't render anything if store state is not initialized
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (this.props.storeState.importType === null) {
      return null;
    }

    return (
      <SpreadsheetBulkImportModal
        onRequestClose={this.handleOnRequestClose}
        importFromSpreadsheet={this.handleImportFromSpreadsheet}
        importItemWord={this.props.importItemWord!}
        title={this.props.title}
        footer={this.props.footer}
        isSchemaRequired={this.props.isSchemaRequired}
        complexPolymerSchemaConfigurations={this.props.complexPolymerSchemaConfigurations}
        maxAllowedRowsToImport={this.props.maxAllowedRowsToImport}
        inferDataTypes={this.props.inferDataTypes}
        {...this.props.storeState}
      />
    );
  }
}

export default SpreadsheetBulkImportModalContainerConnector.connect(SpreadsheetBulkImportModalContainer);
